import { enqueueSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';

import { Box } from '@mui/system';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { LoadingButton } from '@mui/lab';
import Grid from '@mui/system/Unstable_Grid';
import { Card, Stack, CardHeader, IconButton, Typography, CardContent } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useParams, useRouter } from 'src/routes/hooks';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { rootApi } from 'src/services/rootApi';
import { useAppDispatch } from 'src/store/store';
import { PageOptions } from 'src/services/api.types';
import { DueMarker } from 'src/services/tasks/tasks.types';
import { openTaskEditorSidesheet } from 'src/store/slices/app/appSlice';
import PostCommentForm from 'src/layouts/common/threads/post-comment-form';
import PostCommentList from 'src/layouts/common/threads/post-comment-list';
import { ContactLinkEntityType } from 'src/services/contacts/contacts.types';
import { useGetDealQuery, useGetDealTasksQuery, useRemoveDealMutation, useGetDealActivitiesQuery } from 'src/services/deal-flow/deal-flow.service';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import ListSkeleton from 'src/components/list/list-skeleton';
import { ConfirmDialog } from 'src/components/custom-dialog';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import ActivityFeed from 'src/sections/activity/card/activity-feed';
import DealOwnerCard from 'src/sections/deal-flow/views/detail/deal-owner-card';
import DealEditorModal from 'src/sections/deal-flow/components/deal-editor-modal';
import DealDocumentsView from 'src/sections/deal-flow/views/detail/deal-documents';
import PendingTaskManager from 'src/sections/tasks/components/pending-task-manager';
import { DealDetailSection } from 'src/sections/deal-flow/views/detail/detail-card';
import { LinkedContactsCard } from 'src/sections/contacts/components/linked-contacts-card';
import { DealLinkedCompaniesSection } from 'src/sections/deal-flow/views/detail/linked-companies-card';

// ----------------------------------------------------------------------

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 10,
};

enum DealTab {
  DETAIL = 'detail',
  DOCUMENTS = 'documents',

}

export default function DealDetailView() {

  const showTable = useBoolean();

  const { t } = useTranslate();

  const dispatch = useAppDispatch();

  const { deal_id } = useParams();

  const router = useRouter();

  const dealEditorControl = useBoolean();

  const confirmDelete = useBoolean();

  const [currentTab, setCurrentTab] = useState<DealTab | undefined>();

  const [searchParams, setSearchParams] = useSearchParams();

  const [activitiesPage, setActivitiesPage] = useState<PageOptions>(DEFAULT_PAGE);

  const [removeDeal, { isLoading: isDeletingDeal }] = useRemoveDealMutation();

  const tenantType = useOrgTenant();

  const { data: dealTasks, isLoading: isLoadingTasks } = useGetDealTasksQuery({
    dealId: deal_id as string,
    page: DEFAULT_PAGE.page,
    perPage: DEFAULT_PAGE.per_page,
    dueMarker: DueMarker.UPCOMING
  });

  const { currentData: activities } = useGetDealActivitiesQuery(
    {
      dealId: deal_id as string,
      page: activitiesPage.page,
      perPage: activitiesPage.per_page,
    },
    {
      skip: !deal_id
    }
  );

  const { currentData: deal, isLoading: isLoadingDeal } = useGetDealQuery({ dealId: deal_id as string }, {
    skip: !deal_id,
  });

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (currentTab) return;

    let defaultTab: DealTab = DealTab.DETAIL

    const existingTab = searchParams.get('tab');
    if (existingTab && Object.values(DealTab).includes(existingTab as DealTab)) {
      defaultTab = existingTab as DealTab;
    }

    setCurrentTab(defaultTab);

  }, [currentTab, tenantType, searchParams]);

  const handleChangeTab = useCallback((_event: React.SyntheticEvent, newValue: DealTab) => {
    setCurrentTab(newValue);
  }, []);


  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!currentTab) return;

    setSearchParams({ "tab": currentTab }, { replace: true })

  }, [currentTab, setSearchParams]);

  const handleTaskCompleted = useCallback(() => {
    dispatch(rootApi.util.invalidateTags([{ type: 'DealTasks', id: deal_id as string }]));
  }, [deal_id, dispatch]);

  const onCreateTask = useCallback(() => {
    dispatch(openTaskEditorSidesheet({
      dealId: deal_id as string,
    }));
  }, [dispatch, deal_id]);

  const onDeleteDeal = async () => {

    if (!deal_id) return;

    try {

      await removeDeal({
        dealId: deal_id
      }).unwrap();

      confirmDelete.onFalse();

      enqueueSnackbar(t('deal_flow.api.delete_deal.success'), { variant: 'success' });

      router.push(paths.dashboard.deal_flow.root);
    } catch (e) {
      enqueueSnackbar(t('deal_flow.api.delete_deal.default_error'), { variant: 'error' });
    }
  };

  const renderHead = (
    <Card sx={{ flexGrow: 0, boxShadow: 'unset', pb: 1 }}>
      <CardHeader
        sx={{ pl: 0, width: '100%' }}
        title={
          <Stack direction="row" justifyContent="start" alignItems="center" spacing={1}>
            <Box>
              <TruncatedTextField
                text={deal ? deal.title : "Loading..."}
                limit={40}
                mode='characters'
                variant="h3"
                showMoreEnabled={false}
                sx={{ width: '100%' }}
              />
            </Box>
            {
              deal?.type && (
                <Label variant="soft" mt={0.5}>{t(`enums.deal_type.${deal?.type}`)}</Label>
              )
            }
          </Stack>
        }
        action={
          <Stack direction="row" alignItems="center" mt={2}>

            {
              deal && (
                <Label variant="soft" sx={{ bgcolor: `${deal.stage.color}`, color: 'white' }} >
                  {deal.stage.name}
                </Label>
              )
            }

            <IconButton
              sx={{ ml: 1 }}
              onClick={dealEditorControl.onTrue}
            >
              <Iconify icon="lucide:edit" color="black" />
            </IconButton>

            <IconButton
              onClick={() => confirmDelete.onTrue()}
            >
              <Iconify icon="lucide:trash-2" color="red" />
            </IconButton>

          </Stack>
        }
      />
    </Card>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab} sx={{ pl: 2, mb: 2 }}>

      <Tab
        key={DealTab.DETAIL}
        iconPosition="end"
        value={DealTab.DETAIL}
        label={t('deal_flow.detail.tabs.detail')}
      />

      <Tab
        key={DealTab.DOCUMENTS}
        iconPosition="end"
        value={DealTab.DOCUMENTS}
        label={t('deal_flow.detail.tabs.documents')}
      />

    </Tabs>
  );

  const renderPendingTasks = (
    <PendingTaskManager
      tasks={dealTasks?.results || []}
      loading={isLoadingTasks}
      onTaskCompleted={handleTaskCompleted}
      onAddTask={onCreateTask}
    />
  );

  if (isLoadingDeal) {
    return (
      <Grid container spacing={3}>
        <Grid xs={12} md={8}>
          <ListSkeleton />
        </Grid>
        <Grid xs={12} md={4}>
          <Stack direction="column" spacing={2}>
            <ListSkeleton />
            <ListSkeleton />
          </Stack>
        </Grid>
      </Grid>
    );
  }

  if (!deal) {
    return (
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('deal_flow.detail.no_deal_found')}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {t('deal_flow.detail.no_deal_found_message')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Grid container>
        <Grid xs={12}>
          {renderHead}
        </Grid>
      </Grid>

      {renderTabs}

      {
        currentTab === DealTab.DETAIL && (
          <Grid container spacing={3}>
            <Grid xs={12} md={8}>

              <Stack direction="column" spacing={2}>

                <DealDetailSection dealId={deal_id as string} />

                {
                  deal?.thread?.id && (
                    <Card>
                      <CardHeader
                        title={
                          <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
                            <Iconify icon="uil:notes" />
                            <Box>{t('contacts.detail.thread.title')}</Box>
                          </Stack>
                        }
                      />
                      <CardContent>
                        <PostCommentForm thread_id={deal.thread?.id} />
                        <PostCommentList thread_id={deal.thread?.id} />
                      </CardContent>
                    </Card>
                  )
                }

              </Stack>
            </Grid>
            <Grid xs={12} md={4}>
              <Stack spacing={3}>

                {
                  !!dealTasks?.count && (
                    <Box>
                      {renderPendingTasks}
                    </Box>
                  )
                }

                <DealOwnerCard dealId={deal_id as string} />
                <DealLinkedCompaniesSection dealId={deal_id as string} />
                <LinkedContactsCard entityId={deal_id as string} entityType={ContactLinkEntityType.DEAL} enableTypeSelection />

                {
                  !dealTasks?.count && (
                    <Box>
                      {renderPendingTasks}
                    </Box>
                  )
                }

                <Card>
                  <CardHeader title={t('admin-connections.detail.activities')} />
                  <CardContent>
                    <ActivityFeed
                      activities={activities?.results ?? []}
                      page={activitiesPage.page}
                      perPage={activitiesPage.per_page}
                      onPageChange={(e: React.ChangeEvent<unknown>, newPage: number) =>
                        setActivitiesPage({
                          ...activitiesPage,
                          page: newPage
                        })
                      }
                      totalItems={activities?.count ?? 0}
                    />
                  </CardContent>
                </Card>

              </Stack>
            </Grid>
          </Grid>
        )
      }

      {
        currentTab === DealTab.DOCUMENTS && (
          <Box sx={{ px: 2, pb: 2 }}>
            <DealDocumentsView dealId={deal_id as string} />
          </Box>
        )
      }

      {
        (deal && deal.pipeline) && (
          <DealEditorModal
            open={dealEditorControl.value}
            onClose={dealEditorControl.onFalse}
            pipelineId={deal?.pipeline?.id as string}
            dealId={deal?.id as string}
          />
        )
      }

      <ConfirmDialog
        open={confirmDelete.value}
        onClose={confirmDelete.onFalse}
        title={t('deal_flow.delete_confirm.title')}
        content={t('deal_flow.delete_confirm.content')}
        action={
          <LoadingButton variant="contained" color="error" onClick={onDeleteDeal} loading={isDeletingDeal}>
            {t('common.delete')}
          </LoadingButton>
        }
      />

    </>
  );
}